/**
 * NotificationCenter — 알림센터(벨 + 미읽음 배지 + 드롭다운)
 *
 * 동적 메타모델 원칙(절대):
 * - 모듈명 분기 금지. 알림의 typeCode/itemId 메타로 동적 라우팅
 *   (`/:workspace/m/:typeCode/:itemId`). 모듈별 처리 분기 없음.
 * - 심각도(긴급/주의/정보)는 NotificationSeverity 코드값 → 색상/라벨 i18n 매핑.
 * - 시각은 서버 응답(UTC) → KST(dayjs) 표시. 표시 문자열 i18n.
 * - 엔드포인트 부재 시 useNotifications가 빈 목록(graceful).
 *
 * UI: HTML 시안 NotifDropdown 마크업 1:1(.tb-icon-btn/.dropdown/.notif-item). 외부 위젯 비의존.
 */
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Icon } from "@/components/ui/Icon";
import { resolveLabel } from "@/lib/i18n/resolveLabel";
import { useLang } from "@/lib/i18n/useLang";
import { NotificationSeverity } from "@eqms/shared-types";
import {
  useNotifications,
  useMarkNotificationRead,
  type NotificationItem,
} from "@/lib/queries/useNotifications";

/** 심각도 코드 → 표시 색상(시안 토큰) */
const SEVERITY_COLOR: Record<string, string> = {
  [NotificationSeverity.CRITICAL]: "var(--crit)",
  [NotificationSeverity.WARNING]: "var(--warn)",
  [NotificationSeverity.INFO]: "var(--info)",
};

/** UTC 저장값 → KST 표시 */
function toKst(value: string): string {
  const d = dayjs(value);
  return d.isValid() ? d.tz("Asia/Seoul").format("YYYY-MM-DD HH:mm") : value;
}

export function NotificationCenter() {
  const { t } = useTranslation();
  // 알림 제목·본문은 저장된 언어별 맵에서 보는 사람의 현재 언어로 고른다(언어를 바꾸면 즉시 다시 그려짐).
  const { lang } = useLang();
  const navigate = useNavigate();
  const { workspace: slug } = useParams<{ workspace: string }>();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: notifications, isLoading } = useNotifications(slug);
  const markRead = useMarkNotificationRead(slug);

  const items = notifications ?? [];
  // 시스템 알림 미읽음(헤더 벨 배지·드롭다운 "N 신규").
  const unread = items.filter((n) => !n.readAt).length;
  const newCount = unread;

  // 바깥 클릭 시 닫기 (시안 동작)
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const handleClick = (n: NotificationItem) => {
    if (!n.readAt) markRead.mutate(n.id);
    if (slug && n.typeCode) {
      navigate(
        n.itemId
          ? `/${slug}/w/${n.typeCode.toLowerCase()}/${n.itemId}`
          : `/${slug}/w/${n.typeCode.toLowerCase()}`,
      );
      setOpen(false);
    }
  };

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button
        className="tb-icon-btn"
        aria-label={t("notifications.title", "알림")}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="bell" size={19} />
        {unread > 0 ? (
          <span className="bell-count">{unread > 99 ? "99+" : unread}</span>
        ) : (
          <span className="badge-dot" />
        )}
      </button>

      {open && (
        <div className="dropdown">
          <div
            className="between"
            style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}
          >
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              {t("notifications.title", "알림")}
            </span>
            {newCount > 0 && (
              <span className="pill crit">
                <span className="dot" />
                {t("notifications.unreadCount", "{{count}} 신규", { count: newCount })}
              </span>
            )}
          </div>

          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {/* 시스템 알림 그룹 (실 알림엔진 — 승인 대기·기한 임박·교육 배정·심사 일정 등). */}
            <div className="notif-sec">{t("notifications.systemGroup", "시스템 알림")}</div>
            {isLoading ? (
              <div
                className="muted"
                style={{ padding: "20px 16px", textAlign: "center", fontSize: 13 }}
              >
                {t("common.loading", "로딩 중...")}
              </div>
            ) : items.length === 0 ? (
              <div
                className="muted"
                style={{ padding: "20px 16px", textAlign: "center", fontSize: 13 }}
              >
                {t("notifications.empty", "알림이 없습니다")}
              </div>
            ) : (
              items.map((n) => {
                const color =
                  SEVERITY_COLOR[n.severity ?? NotificationSeverity.INFO] ?? "var(--info)";
                return (
                  <div
                    key={n.id}
                    className="notif-item"
                    style={{ cursor: "pointer", opacity: n.readAt ? 0.6 : 1 }}
                    onClick={() => handleClick(n)}
                  >
                    <span className="nd" style={{ background: color }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* 시스템 알림 제목 — 목업: 심각도 배지 없음(규칙기반 자격 알림만 배지 표기).
                          좌측 점(.nd)이 심각도 색을 담당. */}
                      <div style={{ fontSize: 13, fontWeight: n.readAt ? 500 : 600 }}>
                        {resolveLabel(n.title, n.titleI18n, lang)}
                      </div>
                      <div
                        className="muted"
                        style={{
                          fontSize: 12.5,
                          marginTop: 2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {resolveLabel(n.message, n.messageI18n, lang)}
                      </div>
                      <div className="muted mono" style={{ fontSize: 11, marginTop: 4 }}>
                        {toKst(n.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={{ padding: "11px 16px", textAlign: "center" }}>
            <button
              className="btn ghost sm"
              style={{ width: "100%" }}
              onClick={() => setOpen(false)}
            >
              {t("common.close", "닫기")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
